import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// axios.defaults.baseURL = "http://192.168.0.251:21445";
axios.defaults.baseURL = "https://api.nijhuistracking.nl";
axios.defaults.withCredentials = true;

const SignIn = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [organizationCode, setOrganizationCode] = useState('');
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showResetFields, setShowResetFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false); // New state to track login success

  const navigate = useNavigate();

  useEffect(() => {
    // Redirect only if login was successful
    if (loginSuccess) {
      navigate('/'); // Redirect to the dashboard
    }
  }, [loginSuccess, navigate]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/api/auth/login', { email, password });
      if (response.status === 200) {
        setLoginSuccess(true); // Set login success to true to trigger redirect
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      setLoginSuccess(false); // Reset login success in case of error
    } finally {
      setLoading(false); // Ensure loading is set to false after attempting login
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    try {
      await axios.post('/api/auth/register', { email, password, organizationCode });
      setIsSignUp(false); // Switch back to Sign In after successful sign-up
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    }
  };

  const handleForgotPassword = async () => {
    setForgotMessage('');
    setError('');
    try {
      await axios.post('/api/auth/forgot-password', { email: forgotEmail });
      setForgotMessage('A reset code has been sent to your email.');
      setShowResetFields(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset code. Please try again.');
    }
  };

  const handleResetPassword = async () => {
    setForgotMessage('');
    setError('');
    try {
      await axios.post('/api/auth/reset-password-with-code', {
        email: forgotEmail,
        code: resetCode,
        newPassword,
      });
      setForgotMessage('Password reset successful. Please sign in with your new password.');
      setShowResetFields(false);
      setShowForgotPassword(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please check the code and try again.');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden flex justify-center">
        <div className="absolute bottom-0 bg-orange-100 h-1/3 w-full rounded-tl-full rounded-tr-full"></div>
      </div>

      <div className={"relative z-10 w-full max-w-md p-8 space-y-6 bg-white shadow-lg rounded-lg"}>
        <img src={"logo-bold.png"} alt="Nijhuis Engineering" className="mx-auto w-72 mb-4" />
        <h1 className="text-2xl font-bold text-center text-gray-800">Welkom bij Nijhuis Tracking</h1>

        <div className="flex justify-center mt-6 space-x-4">
          <button
            onClick={() => setIsSignUp(false)}
            className={`px-4 py-2 font-semibold ${!isSignUp ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500'}`}
          >
            Inloggen
          </button>
          <button
            onClick={() => setIsSignUp(true)}
            className={`px-4 py-2 font-semibold ${isSignUp ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500'}`}
          >
            Registreren
          </button>
        </div>

        <form onSubmit={isSignUp ? handleSignUp : handleSignIn} className="space-y-4">
          <div>
            <label className="block text-gray-600">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full p-2 mt-1 border border-gray-300 rounded-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
              placeholder="Vul je email in"
            />
          </div>
          <div>
            <label className="block text-gray-600">Wachtwoord</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full p-2 mt-1 border border-gray-300 rounded-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
              placeholder="Vul je wachtwoord in"
            />
          </div>

          {isSignUp && (
            <>
              <div>
                <label className="block text-gray-600">Wachtwoord</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full p-2 mt-1 border border-gray-300 rounded-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                  placeholder="Herhaal je wachtwoord"
                />
              </div>
              <div>
                <label className="block text-gray-600">Organization Code</label>
                <input
                  type="text"
                  value={organizationCode}
                  onChange={(e) => setOrganizationCode(e.target.value)}
                  required
                  className="w-full p-2 mt-1 border border-gray-300 rounded-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                  placeholder="Vul je organization code in"
                />
              </div>
            </>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}
          {forgotMessage && <p className="text-green-500 text-sm">{forgotMessage}</p>}

          <button
            type="submit"
            className="w-full p-2 mt-4 font-semibold text-white bg-orange-500 rounded-sm hover:bg-orange-600 focus:outline-hidden focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50"
          >
            {loading ? 'Loading...' : isSignUp ? 'Sign Up' : 'Sign In'}
          </button>

          {!isSignUp && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-orange-500 hover:underline"
              >
                Wachtwoord Vergeten?
              </button>
            </div>
          )}
        </form>

        {showForgotPassword && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-sm shadow-lg w-full max-w-md">
              <h2 className="text-xl font-bold mb-4 text-center">Wachtwoord Vergeten</h2>
              <input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="w-full p-2 mb-4 border border-gray-300 rounded-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                placeholder="Vul je email in"
              />
              {showResetFields ? (
                <>
                  <input
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    className="w-full p-2 mb-4 border border-gray-300 rounded-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="Vul de reset code in"
                  />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full p-2 mb-4 border border-gray-300 rounded-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    placeholder="Vul je nieuwe wachtwoord in"
                  />
                  <button
                    onClick={handleResetPassword}
                    className="w-full p-2 mb-2 font-semibold text-white bg-orange-500 rounded-sm hover:bg-orange-600"
                  >
                    Reset Wachtwoord
                  </button>
                </>
              ) : (
                <button
                  onClick={handleForgotPassword}
                  className="w-full p-2 mb-2 font-semibold text-white bg-orange-500 rounded-sm hover:bg-orange-600"
                >
                  Stuur reset code
                </button>
              )}
              <button
                onClick={() => setShowForgotPassword(false)}
                className="w-full p-2 mt-2 font-semibold text-gray-500 rounded-sm border border-gray-300 hover:bg-gray-100"
              >
                Annuleren
              </button>
              {forgotMessage && <p className="text-green-500 text-sm text-center mt-4">{forgotMessage}</p>}
              {error && <p className="text-red-500 text-sm text-center mt-4">{error}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignIn;
